<template>
  <div class="mt-3 billing-container">
    <billing-document-filters
      v-model="filter"
      :hasFilters="hasFilters"
      :loading="loadingMoreCharges"
      :showing="showing"
      ref="filterHeight"
    />
    <div class="mt-4 mb-1 w-100 d-flex flex-row justify-content-end">
      <div class="d-flex flex-row justify-content-end align-items-start">
        <div class="d-flex flex-row justify-content-end align-items-center">
          <top-paginator
            :total="listTotal"
            :page="filter.page"
            :perPage="filter.pageSize"
            @change-page="changePage"
          />
        </div>
      </div>
    </div>
    <div
      class="clients-list"
      :class="hasFilters ? 'clients-list-filtered' : ''"
      :style="`--filter-height:` + filterHeight()"
    >
      <grid
        ref="grid"
        :columns="columns"
        :data="items"
        :store="gridConfig.store"
        :selectionMode="gridConfig.selectionMode"
        :columnLines="gridConfig.columnLines"
        :groupFeature="false"
        :autoHeight="true"
        :rowHeight="55"
        :searchFeature="true"
        :stripeFeature="false"
        @storesort="listeners.storeSort"
        @selectionchange="listeners.selectionChange"
        @renderrows="hookDropdownButtons"
      />
    </div>
    <billing-document-new-invoice
      :v-if="newInvoiceModal"
      :showing="newInvoiceModal"
      v-model="newInvoiceModal"
      @createNewInvoice="createNewInvoice"
      @cancelNewInvoice="cancelNewInvoice"
    />
    <billing-document-new-statement
      :v-if="newStatementModal"
      :showing="newStatementModal"
      v-model="newStatementModal"
      @createNewStatement="createNewStatement"
      @cancelNewStatement="cancelNewStatement"
    />
    <billing-document-add-edit-invoice
      :v-if="invoiceAddEditModal.show"
      :showing="invoiceAddEditModal.show"
      v-model="invoiceAddEditModal.show"
      :id="invoiceAddEditModal.id"
      :episodeOfCareId="invoiceAddEditModal.episodeOfCareId"
      :encounterIds="invoiceAddEditModal.encounterIds"
      @close="closeNewInvoiceAddEditModal"
    />
    <billing-document-show-invoice
      v-if="invoiceShowModal.show"
      :showing="invoiceShowModal.show"
      v-model="invoiceShowModal.show"
      :id="invoiceShowModal.id"
      @close="closeNewInvoiceShowModal"
      @edit="editNewInvoiceShowModal"
      @payment="
        invoiceData => {
          this.closeNewInvoiceShowModal();
          this.showPaymentModal({
            ...invoiceData,
            invoice: { id: invoiceData.id }
          });
        }
      "
    />
    <payment-add-edit-modal
      :transactionType="'Payment'"
      :formTitle="`Add payment for ${paymentAddModal.patient.name}`"
      v-if="paymentAddModal.show"
      v-model="paymentAddModal.show"
      :patientId="paymentAddModal.patient.patientId"
      :invoiceId="paymentAddModal.invoiceId"
      @onHidden="
        () => {
          paymentAddModal.show = false;
          fetchInitialData();
        }
      "
      @success="
        () => {
          paymentAddModal.show = false;
          fetchInitialData();
        }
      "
    />
    <billing-document-add-statement
      :v-if="statementAddModal.show"
      :showing="statementAddModal.show"
      v-model="statementAddModal.show"
      :episodeOfCareId="statementAddModal.episodeOfCareId"
      :dateFrom="statementAddModal.dateFrom"
      :dateTo="statementAddModal.dateTo"
      @closeModal="closeNewStatementAddModal"
    />
    <billing-document-show-statement
      v-if="statementShowModal.show"
      v-model="statementShowModal.show"
      :showing="statementShowModal.show"
      :statementId="statementShowModal.id"
      @closeModal="closeNewstatementShowModal"
    />
  </div>
</template>

<script>
import { formatEndDateSql, formatStartDateSql } from "../../../helpers/date";
import { mapActions } from "vuex";
import Grid from "../../../third_party/grid/Grid.vue";
import TopPaginator from "../../controls/TopPaginator.vue";
import billingDocServer from "../../../server/billingDocument";
import BillingDocumentFilters from "./BillingDocumentIndexFilters.vue";
import BillingDocumentNewInvoice from "./BillingDocumentNewInvoice.vue";
import BillingDocumentNewStatement from "./BillingDocumentNewStatement.vue";
import BillingDocumentAddEditInvoice from "./BillingDocumentInvoiceAddEditModal.vue";
import BillingDocumentShowInvoice from "./BillingDocumentInvoiceViewModal.vue";
import PaymentAddEditModal from "../billing_transactions/PaymentAddEditModal.vue";
import BillingDocumentAddStatement from "./BillingDocumentStatementAddModal.vue";
import BillingDocumentShowStatement from "./BillingDocumentStatementShowModal.vue";

export default {
  name: "BillingDocument",
  props: ["showing", "invoiceModalShow", "statementModalShow"],
  components: {
    BillingDocumentShowInvoice,
    BillingDocumentAddEditInvoice,
    BillingDocumentNewInvoice,
    BillingDocumentNewStatement,
    Grid,
    TopPaginator,
    BillingDocumentFilters,
    PaymentAddEditModal,
    BillingDocumentAddStatement,
    BillingDocumentShowStatement
  },
  data() {
    const self = this;
    return {
      filter: {
        patientId: [],
        dateFrom: null,
        dateTo: null,
        createdBy: [],
        ccOnFile: [],
        payments: [],
        searchPhrase: "",
        page: 0,
        pageSize: 50,
        orderBy: "createdAt",
        orderByDescending: true
      },
      loadingMoreCharges: false,
      list: [],
      listTotal: 0,
      refetchBillings: true,
      gridConfig: {
        minHeight: "20em",
        selectionMode: {
          row: false,
          checkbox: false,
          rowCheckboxSelection: false,
          showCheckAll: false
        },
        columnLines: false,
        store: {
          // Default sorters
          sorters: [{ field: "dateAndTime", ascending: false }]
        }
      },
      listeners: {
        selectionChange: selectionEvent => {
          self.billingsSelected = selectionEvent.selection.map(x => x.data);
        },
        storeSort: sortData => {
          if (sortData.sorters.length === 1) {
            const sorter = sortData.sorters[0];
            const ascending = sorter.ascending;
            self.filter.orderBy = sorter.field;
            self.filter.orderByDescending = !ascending;
            self.changePage(0);
          }
        }
      },
      billingsSelected: [],
      paymentAddModal: {
        show: false,
        patient: null,
        invoiceId: null
      },
      invoiceAddEditModal: {
        show: false,
        id: null,
        episodeOfCareId: null,
        encounterIds: null
      },
      invoiceShowModal: {
        show: false,
        id: null
      },
      statementAddModal: {
        show: false,
        episodeOfCareId: null,
        dateFrom: null,
        datetO: null
      },
      statementShowModal: {
        show: false,
        id: null
      }
    };
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess", "showError", "show", "hide"]),
    async fetchInitialData() {
      await this.fetchBillings(true);
    },
    fetchBillings: async function(instantReload = false) {
      if (this.lastTimeout) {
        clearTimeout(this.lastTimeout);
        this.lastTimeout = null;
      }
      this.lastTimeout = setTimeout(
        async () => {
          let result = await billingDocServer.queryImproved({
            PatientId: this.filter.patientId?.map(s => s.value),
            searchPhrase: this.filter.searchPhrase,
            dateFrom: formatStartDateSql(this.filter.dateFrom),
            dateTo: formatEndDateSql(this.filter.dateTo),
            createdByIds: this.filter.createdBy.map(user => user.value),
            ccOnFile:
              this.filter.ccOnFile.length === 1
                ? this.filter.ccOnFile[0].value === "yes"
                : null,
            paid:
              this.filter.payments.length === 0 ||
              this.filter.payments.length === 2
                ? null
                : this.filter.payments.some(x => x.value === "paid"),
            unpaid: this.filter.payments.some(x => x.value === "unpaid"),
            page: this.filter.page,
            pageSize: this.filter.pageSize,
            orderBy: this.filter.orderBy,
            orderByDescending: this.filter.orderByDescending
          });
          this.list = await result.documents;
          this.listTotal = result.count;
        },
        instantReload ? 0 : 1000
      );
    },
    filterHeight() {
      return (
        (this.$refs.filterHeight
          ? this.$refs.filterHeight.$el.clientHeight
          : 0) + "px"
      );
    },
    changePage(page) {
      this.filter.page = page;
      this.fetchBillings(true);
      this.scrollGridToTop();
    },
    scrollGridToTop() {
      const el = document.getElementsByClassName("b-vertical-overflow");
      Array.from(el).forEach(e => {
        if (e.scrollTop > 0) e.scrollTop = 0;
      });
    },
    async createNewInvoice(id, encounterIds, episodeOfCareId) {
      this.invoiceAddEditModal.id = id;
      this.invoiceAddEditModal.episodeOfCareId = episodeOfCareId;
      this.invoiceAddEditModal.encounterIds = encounterIds;
      this.$emit("changeModalShow");
      this.invoiceAddEditModal.show = true;
    },
    async cancelNewInvoice() {
      this.$emit("changeModalShow");
      await this.fetchInitialData(true);
    },
    async createNewStatement(episodeOfCareId, dateFrom, dateTo) {
      this.statementAddModal.episodeOfCareId = episodeOfCareId;
      this.statementAddModal.dateFrom = dateFrom;
      this.statementAddModal.dateTo = dateTo;
      this.$emit("changeStatementModalShow");
      this.statementAddModal.show = true;
    },
    async cancelNewStatement() {
      this.$emit("changeStatementModalShow");
      await this.fetchInitialData(true);
    },
    async closeNewInvoiceAddEditModal() {
      this.invoiceAddEditModal.show = false;
      await this.fetchInitialData(true);
    },
    async closeNewStatementAddModal() {
      this.statementAddModal.show = false;
      await this.fetchInitialData(true);
    },
    async closeNewInvoiceShowModal() {
      this.invoiceShowModal.show = false;
      await this.fetchInitialData(true);
    },
    async closeNewstatementShowModal() {
      this.statementShowModal.show = false;
      await this.fetchInitialData(true);
    },
    async editNewInvoiceShowModal(data) {
      this.invoiceShowModal.show = false;
      this.invoiceAddEditModal.id = data;
      this.invoiceAddEditModal.show = true;
    },
    showPaymentModal(data) {
      this.paymentAddModal.invoiceId = data.invoice.id.toLowerCase();
      this.paymentAddModal.patient = {
        patientId: data.episodeOfCare.patientId,
        name: this.formatClient(
          data.episodeOfCare.patient.firstName ?? "",
          data.episodeOfCare.patient.middleName ?? "",
          data.episodeOfCare.patient.preferredName ?? "",
          data.episodeOfCare.patient.lastName ?? "",
          ""
        )
      };
      this.paymentAddModal.show = true;
    }
  },
  computed: {
    hasFilters() {
      return (
        this.filter.patientId.length > 0 ||
        this.filter.dateFrom ||
        this.filter.dateTo ||
        this.filter.createdBy.length > 0 ||
        this.filter.payments.length > 0 ||
        this.filter.ccOnFile.length > 0
      );
    },
    items() {
      return this.list.map(item => {
        return {
          ...item
        };
      });
    },
    columns() {
      const self = this;
      return [
        {
          text: "Patient",
          field: "patient",
          type: "template",
          flex: 1,
          editor: false,
          template: ({ record }) => {
            return record.data.episodeOfCare != null
              ? this.formatEocObj(record.data.episodeOfCare)
              : "";
          },
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Document",
          field: "document",
          type: "template",
          flex: 1,
          editor: false,
          template: data => {
            let refNumber = "";
            if (data.record.data.statement) {
              refNumber = `Statement #${data.record.data.statement.refNumber ??
                ""}`;
            }
            if (data.record.data.invoice) {
              refNumber = `Invoice #${data.record.data.invoice.refNumber ??
                ""}`;
            }
            return refNumber;
          },
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Created At",
          field: "createdAt",
          type: "template",
          flex: 1,
          editor: false,
          template: data => {
            return data.record.data.createdOn != null
              ? this.formatDateOnly(data.record.data.createdOn)
              : null;
          },
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Created By",
          field: "createdBy",
          type: "template",
          flex: 1,
          editor: false,
          template: data => {
            return data.record.data.createdBy != null
              ? this.formatUser(data.record.data.createdBy)
              : "";
          },
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "CC on file",
          field: "patientCCOnFile",
          type: "template",
          flex: 1,
          editor: false,
          template: data => (data.value ? `Yes` : `No`),
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Details",
          field: "details",
          type: "template",
          flex: 1,
          editor: false,
          template: data => {
            let returnValue = "";
            if (data.record.data.invoice) {
              const unpaid =
                (data.record.data.invoice.amountPaid ?? 0) > 0
                  ? `<div class="d-flex justify-content-start align-items-center w-100">
                    ${this.toCurrencyValue(data.record.data.invoice.amountPaid)}
                     <span style="font-size:10px; padding: 2px 10px;" class="badge-v2 ml-2 badge-success">Paid</span>
                  </div>`
                  : ``;
              const paid =
                (data.record.data.invoice.totalAmount ?? 0) -
                  (data.record.data.invoice.amountPaid ?? 0) >
                0
                  ? `<div class="d-flex justify-content-start align-items-center pt-sm-1">
                      ${this.toCurrencyValue(
                        data.record.data.invoice.totalAmount -
                          data.record.data.invoice.amountPaid
                      )}
                      <span style="font-size:10px; padding: 2px 10px;" class="badge-v2 ml-2 badge-danger">Unpaid</span>
                  </div>`
                  : ``;
              returnValue = `
                <div class="d-flex justify-content-center flex-column w-100  my-1">
                    ${unpaid}
                    ${paid}
                </div>
              `;
            }
            return returnValue;
          }
        },
        {
          text: "",
          type: "action",
          align: "right",
          cellCls: "pointer-cell",
          width: 80,
          actions: [
            {
              tooltip: "View",
              renderer: () =>
                `<i class="fa fa-search-plus color-custom-dark-grey hover-text-primary b-action-item  grid-action-icon"></i>`,
              onClick: ({ record }) => {
                if (record.data.invoice) {
                  this.invoiceShowModal.id = record.data.invoice.id;
                  this.invoiceShowModal.show = true;
                }
                if (record.data.statement) {
                  this.statementShowModal.id = record.data.statement.id;
                  this.statementShowModal.show = true;
                }
              }
            },
            {
              tooltip: "More",
              renderer: ({ record }) => {
                const actions = [];
                if (
                  record.data.invoice &&
                  record.data.invoice.totalAmount !==
                    record.data.invoice.amountPaid
                ) {
                  actions.push({
                    content: "Add Payment",
                    callback: () => {
                      const invoice = record.data;
                      this.showPaymentModal(invoice);
                    }
                  });
                }

                if (record.data.invoice) {
                  actions.push({
                    content: "Edit",
                    callback: () => {
                      this.invoiceAddEditModal.id = record.data.invoice.id;
                      this.invoiceAddEditModal.show = true;
                    }
                  });
                }

                actions.push({
                  content: "Delete",
                  callback: () => {
                    this.$dialog
                      .alert(
                        {
                          title: "Are you sure you want to delete this record?"
                        },
                        {
                          view: "confirm-delete"
                        }
                      )
                      .then(async () => {
                        await billingDocServer
                          .delete(record.data.id)
                          .then(async () => {
                            await this.fetchInitialData();
                            this.showAlertSuccess();
                          })
                          .catch(e => console.error(e.message));
                      });
                  }
                });

                return this.createDropdownButton({
                  items: actions,
                  menuWidth: 150
                });
              }
            }
          ]
        }
      ];
    },
    newInvoiceModal: {
      get() {
        return this.$props.invoiceModalShow;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    newStatementModal: {
      get() {
        return this.$props.statementModalShow;
      },
      set(value) {
        this.$emit("input", value);
      }
    }
  },
  watch: {
    "filter.patientId": function() {
      this.changePage(0);
      this.fetchBillings();
    },
    "filter.dateFrom": function() {
      this.changePage(0);
      this.fetchBillings();
    },
    "filter.dateTo": function() {
      this.changePage(0);
      this.fetchBillings();
    },
    showing: async function(newValue) {
      if (newValue) {
        await this.fetchInitialData();
      }
      if (newValue && this.refetchBillings) {
        this.fetchInitialData(true);
      }
    }
  }
};
</script>

<style scoped>
.filter-select {
  height: 50px;
  width: 300px;
  padding-top: 5px;
}

.clients-list {
  overflow-y: auto;
}

.clients-list {
  height: calc(100vh - 240px - var(--filter-height));
}

.clients-list >>> .b-grid-container .b-container {
  max-height: calc(100vh - 240px - var(--filter-height)) !important;
}

.border-yellow {
  border-color: #ffc107 !important;
}

.separator-or {
  margin-bottom: -13px;
}

.dg-main-content {
  max-width: 550px;
}
</style>

<style>
.filter-select .vs__dropdown-toggle {
  height: 100%;
  border-radius: 0;
  border: 0;
}

.patient-picker-hide-options .vs__dropdown-menu {
  display: none;
}

.patient-picker-hide-options.vs--open .vs__dropdown-toggle {
  border-bottom-color: #4044e6 !important;
  border-bottom-width: 2px;
}
</style>
